import html2canvas from "html2canvas";
import { logger } from "~/common/logger";

export interface DownloadChartOptions {
  element: HTMLDivElement;
  document: Document;
  filename?: string;
}

/**
 * Downloads a chart element as a PNG image using html2canvas
 */
export const downloadChart = async ({
  element,
  document,
  filename = "morning_consult_chart",
}: DownloadChartOptions): Promise<void> => {
  if (!element || !document) return;

  try {
    const canvasOptions = {
      backgroundColor: "#ffffff",
      scale: 2,
      useCORS: true,
      allowTaint: true,
      height: element.scrollHeight + 36,
      width: element.scrollWidth + 48,
      removeContainer: true,
      x: -24,
      y: -16,
      logging: false,
      windowWidth: 1280,
      windowHeight: 3000,
      onclone: (clonedDoc: Document) => {
        const style = clonedDoc.createElement("style");
        style.textContent = `
          @font-face {
            font-family: "Proxima Nova";
            src: url("https://cdn.morningconsultintelligence.com/MCI-1.1/fonts/PN-Regular.woff2") format("woff2");
            font-weight: 400;
            font-style: normal;
          }

          .EverViz {
            padding: 0px 8px !important;
          }

          .highcharts-label > span, .highcharts-legend-item > span {
            top: 0px !important;
            overflow: visible !important;
            min-height: 16px !important;
          }
            
          .everviz-chart-header {
            border-top-width: 0px !important; 
            padding-top: 0px !important;
          }
          
          .everviz-chart-header-container {
            padding-top: 0px !important;
          }

          .canvas-container select {
            padding: 0px 12px !important;
          }
          
          .highcharts-root {
            width: calc(100% + 16px) !important;
          }
          
          * {
            font-family: "Proxima Nova", sans-serif !important;
          }
          `;
        clonedDoc.head.appendChild(style);

        // Convert Next.js Image components to regular img tags for better html2canvas compatibility
        clonedDoc
          .querySelectorAll(".everviz-chart-image-container")
          .forEach((container: Element) => {
            const containerElement = container as HTMLElement;
            const imageElement = containerElement?.querySelector("img");
            if (imageElement && imageElement.src) {
              // Create a new regular img element
              const newImg = clonedDoc.createElement("img");
              newImg.src = imageElement.src;

              // Replace the container content with the new img
              containerElement.innerHTML = "";
              containerElement.appendChild(newImg);
            }
          });

        clonedDoc.querySelectorAll("*").forEach((el: Element) => {
          (el as HTMLElement).style.setProperty(
            "font-family",
            '"Proxima Nova", sans-serif',
            "important"
          );
        });
      },
    };

    const canvas = await html2canvas(element, canvasOptions);

    // Create download link
    const link = document.createElement("a");
    link.download = `${filename}.png`;
    link.href = canvas.toDataURL("image/png");

    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    logger.error(error, "Error downloading chart");
  }
};

/**
 * Generates a filename from a headline string by replacing non-alphanumeric characters
 */
export const generateChartFilename = (headline?: string): string => {
  if (!headline) return "morning_consult_chart";

  // Split into words, take first 5, join with underscores, and make lowercase
  const words = headline.trim().split(/\s+/).slice(0, 5);
  const processedTitle = words
    .join("_")
    .replace(/[^a-z0-9_]/gi, "_")
    .toLowerCase();

  // Clean up multiple consecutive underscores and trim leading/trailing underscores
  const cleanedTitle = processedTitle
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");

  // If the result is empty or only underscores, fall back to default
  if (!cleanedTitle || cleanedTitle.match(/^_*$/)) {
    return "morning_consult_chart";
  }

  return `morning_consult_${cleanedTitle}`;
};
